/** Dependency-free utilities. Everything here is guarded — never throws. */

/** Set true if we had to fall back to weak (Math.random) entropy for an ID. */
export let weakEntropy = false;

export function nowIso(): string {
  try {
    return new Date().toISOString();
  } catch {
    return '' + Date.now();
  }
}

/** RFC-4122-ish v4, hex (no dashes). Falls back gracefully; flags weak entropy. */
export function uuid(): string {
  try {
    const c: Crypto | undefined = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID().replace(/-/g, '');
    }
    if (c && typeof c.getRandomValues === 'function') {
      const b = c.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      let s = '';
      for (let i = 0; i < 16; i++) s += b[i].toString(16).padStart(2, '0');
      return s;
    }
  } catch {
    /* fall through */
  }
  weakEntropy = true;
  return (
    Date.now().toString(16) +
    Math.floor(Math.random() * 0xffffffffffff).toString(16).padStart(12, '0')
  );
}

export function id(prefix: string): string {
  return prefix + '_' + uuid();
}

/** SHA-256 → lowercase hex. Returns null if SubtleCrypto unavailable (insecure ctx). */
export async function sha256hex(input: string): Promise<string | null> {
  try {
    const c: Crypto | undefined = (globalThis as { crypto?: Crypto }).crypto;
    if (!c || !c.subtle) return null;
    const data = new TextEncoder().encode(input);
    const buf = await c.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(buf);
    let hex = '';
    for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, '0');
    return hex;
  } catch {
    return null;
  }
}

export function safeJsonParse<T>(s: string | null | undefined): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

export function safeJsonStringify(v: unknown): string {
  try {
    return JSON.stringify(v, jsonReplacer);
  } catch {
    return '{}';
  }
}

/** Drop functions/undefined/circular-unsafe values defensively. */
function jsonReplacer(_k: string, v: unknown): unknown {
  if (typeof v === 'function' || typeof v === 'undefined') return undefined;
  if (typeof v === 'bigint') return v.toString();
  return v;
}

export function byteLength(s: string): number {
  try {
    return new Blob([s]).size;
  } catch {
    return s.length;
  }
}

/* ----------------------------- cookies ----------------------------- */

export function getCookie(name: string): string | null {
  try {
    const re = new RegExp(
      '(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\/+^])/g, '\\$1') + '=([^;]*)'
    );
    const m = document.cookie.match(re);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

export function setCookie(
  name: string,
  value: string,
  maxAgeSeconds: number,
  domain?: string
): boolean {
  try {
    let c =
      name +
      '=' +
      encodeURIComponent(value) +
      '; Path=/; Max-Age=' +
      Math.floor(maxAgeSeconds) +
      '; SameSite=Lax';
    if (location.protocol === 'https:') c += '; Secure';
    if (domain) c += '; Domain=' + domain;
    document.cookie = c;
    return true;
  } catch {
    return false;
  }
}

export function delCookie(name: string, domain?: string): void {
  try {
    let c = name + '=; Path=/; Max-Age=0; SameSite=Lax';
    if (domain) c += '; Domain=' + domain;
    document.cookie = c;
  } catch {
    /* noop */
  }
}

/* --------------------------- web storage --------------------------- */

export function storageGet(store: Storage | null, key: string): string | null {
  try {
    return store ? store.getItem(key) : null;
  } catch {
    return null;
  }
}

export function storageSet(store: Storage | null, key: string, value: string): boolean {
  try {
    if (!store) return false;
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function storageDel(store: Storage | null, key: string): void {
  try {
    store && store.removeItem(key);
  } catch {
    /* noop */
  }
}

/** Probe a Storage object for actual write availability (ITP/private mode/quota). */
export function probeStorage(getter: () => Storage | undefined): Storage | null {
  try {
    const s = getter();
    if (!s) return null;
    const k = '__tmg_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

/* ------------------------------ urls ------------------------------- */

/** Normalized route key for SPA dedup: path + search (hash ignored). */
export function routeKey(): string {
  try {
    return location.pathname + location.search;
  } catch {
    return '';
  }
}

export function currentUrl(): string {
  try {
    return urlWithoutQueryOrFragment(location.href);
  } catch {
    return '';
  }
}

/**
 * Keep page identity useful for analytics without transmitting query parameters
 * or fragments. Campaign parameters are parsed into their dedicated fields;
 * the raw URL is never their transport.
 */
export function urlWithoutQueryOrFragment(value: string): string {
  if (!value) return '';
  try {
    const parsed = new URL(value, location.href);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return parsed.origin + parsed.pathname;
  } catch {
    return '';
  }
}

export function parseQuery(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const q = search.charAt(0) === '?' ? search.slice(1) : search;
    if (!q) return out;
    for (const pair of q.split('&')) {
      if (!pair) continue;
      const i = pair.indexOf('=');
      const k = decodeURIComponent(i < 0 ? pair : pair.slice(0, i));
      const v = i < 0 ? '' : decodeURIComponent(pair.slice(i + 1).replace(/\+/g, ' '));
      if (k) out[k] = v;
    }
  } catch {
    /* noop */
  }
  return out;
}

export function hostnameOf(url: string): string {
  try {
    return new URL(url, location.href).hostname;
  } catch {
    return '';
  }
}

export function isSameOrigin(url: string): boolean {
  try {
    return new URL(url, location.href).origin === location.origin;
  } catch {
    return false;
  }
}

/* ------------------------------ masks ------------------------------ */

export function maskEmail(e?: string): string {
  if (!e) return '';
  const at = e.indexOf('@');
  if (at < 1) return '***';
  return e[0] + '***@' + (e[at + 1] || '') + '***';
}

export function maskPhone(p?: string): string {
  if (!p) return '';
  const digits = p.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return '***' + digits.slice(-4);
}

export function maskKey(k?: string): string {
  if (!k) return '';
  if (k.length <= 8) return k.slice(0, 2) + '…';
  return k.slice(0, k.lastIndexOf('_') + 1 || 3) + '…' + k.slice(-4);
}
